/**
 * useLiveMeetingSession - WebSocket hook for live meeting intelligence.
 *
 * Opens a persistent WebSocket to GET /ws/live-meeting authenticated via
 * Firebase ID token. Listens for lm:* events and surfaces derived state.
 *
 * Reconnects automatically with exponential backoff when disconnected.
 * The hook is self-contained: it sources workspaceUuid from its argument
 * (provided by LiveMeetingPane after calling useWorkspace()).
 */

import * as React from 'react';
import { apiFetch } from '../../../lib/apiClient';
import { firebaseAuth } from '../../../lib/firebase';
import { fetchTranscriptPage } from '../services/liveMeetingApi';
import type {
  LiveMeetingEvent,
  MeetingRecommendation,
  SystemEventLogEntry,
  TranscriptChunk,
  TranscriptSegment,
  UseLiveMeetingSessionReturn,
} from '../types/live-meeting';

const MAX_TRANSCRIPT_CHUNKS = 200;
const BASE_RECONNECT_DELAY_MS = 2_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 10;

function resolveWsBaseUrl(): string {
  const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
  if (apiUrl) {
    return apiUrl.replace(/^http/, 'ws').replace(/\/$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/^http/, 'ws').replace(/\/$/, '');
  }

  return 'ws://localhost:3001';
}

function backoffDelay(attempt: number): number {
  const delay = BASE_RECONNECT_DELAY_MS * 2 ** attempt;
  return Math.min(delay, MAX_RECONNECT_DELAY_MS);
}

export function useLiveMeetingSession(
  workspaceUuid: string | null | undefined,
  moduleKey?: string | null,
  pinnedSessionUuid?: string | null
): UseLiveMeetingSessionReturn {
  const [isConnected, setIsConnected] = React.useState(false);
  const [sessionUuid, setSessionUuid] = React.useState<string | null>(null);
  const [zoomMeetingUuid, setZoomMeetingUuid] = React.useState<string | null>(null);
  const [segments, setSegments] = React.useState<TranscriptSegment[]>([]);
  const [transcriptChunks, setTranscriptChunks] = React.useState<TranscriptChunk[]>([]);
  const [recommendations, setRecommendations] = React.useState<MeetingRecommendation[]>([]);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState<UseLiveMeetingSessionReturn['status']>('disconnected');
  const [dealUuid, setDealUuid] = React.useState<string | null>(null);
  const [dealTitle, setDealTitle] = React.useState<string | null>(null);
  // Debug: latest raw WS payload per event type - excludes lm:transcript_chunk and lm:system_event
  const [rawEventsByType, setRawEventsByType] = React.useState<Record<string, unknown>>({});
  // Debug: all system events received this session, in arrival order
  const [systemEventLog, setSystemEventLog] = React.useState<SystemEventLogEntry[]>([]);
  // Debug: rolling log of ALL raw lm:transcript_chunk payloads (max 100)
  const [rawTranscriptLog, setRawTranscriptLog] = React.useState<Record<string, unknown>[]>([]);

  const [hasMoreTranscript, setHasMoreTranscript] = React.useState(false);
  const [isLoadingOlderTranscript, setIsLoadingOlderTranscript] = React.useState(false);
  // Tracks current sessionUuid in a ref so callbacks can access it without stale closure.
  const sessionUuidRef = React.useRef<string | null>(null);
  // Index of the earliest entry loaded so far; used as the before-cursor for fetches.
  const olderBeforeIndexRef = React.useRef<number>(0);

  // Gate: only attempt WS when the user has an active Zoom connection (Settings > Connections > Zoom).
  // null = still fetching; false = not connected; true = connected.
  const [zoomConnected, setZoomConnected] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    if (!workspaceUuid) return;
    let cancelled = false;
    void apiFetch<{ connected: boolean }>('/api/zoom/user/status')
      .then((data) => {
        if (!cancelled) setZoomConnected(data.connected);
      })
      .catch(() => {
        if (!cancelled) setZoomConnected(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceUuid]);

  const wsRef = React.useRef<WebSocket | null>(null);
  const mountedRef = React.useRef(false);
  const reconnectAttemptRef = React.useRef(0);
  const reconnectTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set when lm:session_connected arrives; used to compute per-chunk session offset
  const sessionStartedAtRef = React.useRef<number>(0);

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const connect = React.useCallback(async () => {
    if (!mountedRef.current || !workspaceUuid) return;

    const firebaseUser = firebaseAuth.currentUser;
    if (!firebaseUser) return;

    let token: string;
    try {
      token = await firebaseUser.getIdToken();
    } catch {
      if (mountedRef.current) setStatus('error');
      return;
    }

    if (!mountedRef.current) return;

    // Close any existing socket before opening a new one.
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    setStatus('connecting');

    const url = `${resolveWsBaseUrl()}/ws/live-meeting?workspaceUuid=${encodeURIComponent(workspaceUuid)}${moduleKey ? `&moduleKey=${encodeURIComponent(moduleKey)}` : ''}${pinnedSessionUuid ? `&pinnedSessionUuid=${encodeURIComponent(pinnedSessionUuid)}` : ''}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) return;
      // Send auth frame first - token stays out of the URL so it is not
      // visible in browser DevTools network logs.
      ws.send(JSON.stringify({ type: 'auth', token }));
      reconnectAttemptRef.current = 0;
      setIsConnected(true);
      setStatus('waiting');
      setErrorMessage(null);
    };

    ws.onmessage = (event: MessageEvent) => {
      if (!mountedRef.current) return;
      // Capture receive time before any parsing so it reflects actual WS delivery
      const receivedAt = Date.now();
      let msg: LiveMeetingEvent;
      let rawParsed: Record<string, unknown>;
      try {
        rawParsed = JSON.parse(event.data as string) as Record<string, unknown>;
        msg = rawParsed as unknown as LiveMeetingEvent;
      } catch {
        return;
      }
      // Store latest raw payload for each event type for debug inspection.
      // lm:transcript_chunk goes to rawTranscriptLog (rolling log, all events).
      // lm:system_event goes only to systemEventLog (via handleEvent).
      // All other types go into rawEventsByType as latest-per-type.
      const evType = typeof rawParsed.type === 'string' ? rawParsed.type : 'unknown';
      if (evType === 'lm:transcript_chunk') {
        setRawTranscriptLog((prev) => {
          const next = [...prev, rawParsed];
          return next.length > 100 ? next.slice(next.length - 100) : next;
        });
      } else if (evType !== 'lm:system_event') {
        setRawEventsByType((prev) => ({ ...prev, [evType]: rawParsed }));
      }
      handleEvent(msg, receivedAt);
    };

    ws.onclose = () => {
      if (!mountedRef.current) return;
      setIsConnected(false);
      wsRef.current = null;

      if (reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
        const delay = backoffDelay(reconnectAttemptRef.current);
        reconnectAttemptRef.current += 1;
        setStatus('connecting');
        reconnectTimerRef.current = setTimeout(() => {
          void connect();
        }, delay);
      } else {
        setStatus('error');
        setErrorMessage('Connection lost. Please refresh the page.');
      }
    };

    ws.onerror = () => {
      // onclose fires immediately after onerror; let onclose handle reconnect.
    };
  }, [workspaceUuid, moduleKey, pinnedSessionUuid]);

  const handleEvent = (event: LiveMeetingEvent, receivedAt: number) => {
    switch (event.type) {
      case 'lm:session_connected':
        sessionStartedAtRef.current = receivedAt;
        setSessionUuid(event.sessionUuid);
        sessionUuidRef.current = event.sessionUuid;
        setZoomMeetingUuid(event.zoomMeetingUuid);
        setStatus('active');
        setSegments([]);
        setTranscriptChunks([]);
        setRecommendations([]);
        setDealUuid(null);
        setDealTitle(null);
        setSystemEventLog([]);
        setRawTranscriptLog([]);
        setHasMoreTranscript(false);
        setIsLoadingOlderTranscript(false);
        olderBeforeIndexRef.current = 0;
        break;

      case 'lm:transcript_chunk': {
        const segmentId = `${event.userId}-${event.startTime}`;
        const sessionOffset = Math.max(0, receivedAt - sessionStartedAtRef.current);
        const providerToApiMs = Math.max(0, event.sentAt - event.timestamp);
        const apiToUiMs = Math.max(0, receivedAt - event.sentAt);

        // ── Segment upsert (primary transcript path) ──────────────────────
        // Partial results update the existing segment in-place; new utterances
        // are appended. This runs unconditionally regardless of isFinal so the
        // transcript renders immediately for every incoming chunk.
        setSegments((prev) => {
          const existingIdx = prev.findIndex((s) => s.segmentId === segmentId);
          if (existingIdx !== -1) {
            // Update existing segment: text, status, latency - preserve sessionOffsetMs
            const updated = [...prev];
            updated[existingIdx] = {
              ...updated[existingIdx]!,
              text: event.text,
              updatedAt: receivedAt,
              status: event.isFinal ? 'finalized' : 'partial',
              providerToApiMs,
              apiToUiMs,
            };
            return updated;
          }
          // New segment: append
          const next: TranscriptSegment[] = [
            ...prev,
            {
              segmentId,
              speakerId: event.userId,
              speakerName: event.userName,
              text: event.text,
              startedAt: event.startTime,
              updatedAt: receivedAt,
              status: event.isFinal ? 'finalized' : 'partial',
              sessionOffsetMs: sessionOffset,
              providerToApiMs,
              apiToUiMs,
              speakerRole: event.participantRole,
            },
          ];
          return next.length > MAX_TRANSCRIPT_CHUNKS
            ? next.slice(next.length - MAX_TRANSCRIPT_CHUNKS)
            : next;
        });

        // ── Backward-compat finalized chunk list (coaching debounce) ──────────
        // Only append once the utterance is finalized so callers that count
        // final chunks (e.g. deal-coaching debounce in LiveMeetingPane) work correctly.
        if (event.isFinal) {
          const chunk: TranscriptChunk = {
            userId: event.userId,
            userName: event.userName,
            text: event.text,
            timestamp: event.timestamp,
            sessionOffsetMs: sessionOffset,
            providerToApiMs,
            apiToUiMs,
            isFinal: true,
            role: event.participantRole,
          };
          setTranscriptChunks((prev) => {
            const next = [...prev, chunk];
            return next.length > MAX_TRANSCRIPT_CHUNKS
              ? next.slice(next.length - MAX_TRANSCRIPT_CHUNKS)
              : next;
          });
        }
        break;
      }

      case 'lm:recommendation':
        setRecommendations((prev) => [
          ...prev,
          {
            ruleKey: event.ruleKey,
            triggerTerm: event.triggerTerm,
            triggerText: event.triggerText,
            suggestions: event.suggestions,
            confidence: event.confidence,
            timestamp: Date.now(),
          },
        ]);
        break;

      case 'lm:session_ended':
        setStatus('ended');
        break;

      case 'lm:error':
        setErrorMessage(event.message);
        setStatus('error');
        break;

      case 'lm:deal_linked':
        setDealUuid(event.dealUuid);
        setDealTitle(event.dealTitle);
        break;

      case 'lm:transcript_page_info': {
        // Initial replay page info - sets the cursor for subsequent HTTP fetches.
        const before = Math.max(0, event.totalEntries - event.sentEntries);
        olderBeforeIndexRef.current = before;
        setHasMoreTranscript(before > 0);
        break;
      }

      case 'lm:system_event': {
        // System events are appended as special segments (participant joined/left).
        // They are always "finalized" and use the participantId as the speaker key.
        const systemSegmentId = `system-${event.eventType}-${event.participantId}-${event.timestamp}`;
        setSegments((prev) => {
          // Idempotency: skip if a segment with this ID already exists
          if (prev.some((s) => s.segmentId === systemSegmentId)) return prev;
          const withSystem: TranscriptSegment[] = [
            ...prev,
            {
              segmentId: systemSegmentId,
              speakerId: event.participantId,
              speakerName: event.participantName,
              text: event.text,
              startedAt: event.timestamp,
              updatedAt: receivedAt,
              status: 'finalized',
              sessionOffsetMs: Math.max(0, receivedAt - sessionStartedAtRef.current),
              providerToApiMs: 0,
              apiToUiMs: Math.max(0, receivedAt - event.timestamp),
              isSystemEvent: true,
              eventType: event.eventType,
            },
          ];

          // Back-fill speakerRole on any speech segments for this participant that
          // arrived before the participant_joined webhook populated participantRoles.
          // Only applies to joined/before-host events (leave events don't change role).
          const next =
            event.eventType !== 'zoom.participant_left'
              ? withSystem.map((s) =>
                  !s.isSystemEvent &&
                  // Coerce both sides to string: RTMS may deliver user_id as a number
                  // at runtime despite the TypeScript type saying string, while the
                  // webhook participantId is always a quoted string.
                  String(s.speakerId) === String(event.participantId) &&
                  s.speakerRole === undefined
                    ? { ...s, speakerRole: event.participantRole }
                    : s
                )
              : withSystem;

          return next.length > MAX_TRANSCRIPT_CHUNKS
            ? next.slice(next.length - MAX_TRANSCRIPT_CHUNKS)
            : next;
        });
        setSystemEventLog((prev) => [...prev, event]);
        break;
      }
    }
  };

  React.useEffect(() => {
    if (!workspaceUuid) return;
    if (zoomConnected !== true) return; // wait until Zoom connection is confirmed

    mountedRef.current = true;
    reconnectAttemptRef.current = 0;
    void connect();

    return () => {
      mountedRef.current = false;
      clearReconnectTimer();
      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [workspaceUuid, zoomConnected, connect]);

  const refreshZoomStatus = React.useCallback(() => {
    if (!workspaceUuid) return;
    void apiFetch<{ connected: boolean }>('/api/zoom/user/status')
      .then((data) => {
        setZoomConnected(data.connected);
      })
      .catch(() => {
        setZoomConnected(false);
      });
  }, [workspaceUuid]);

  const loadOlderTranscript = React.useCallback(() => {
    if (isLoadingOlderTranscript) return;
    if (olderBeforeIndexRef.current <= 0) return;

    const resolvedSessionUuid = sessionUuidRef.current ?? pinnedSessionUuid ?? null;
    if (!resolvedSessionUuid) return;

    const before = olderBeforeIndexRef.current;
    setIsLoadingOlderTranscript(true);

    void fetchTranscriptPage(resolvedSessionUuid, before)
      .then((result) => {
        if (!mountedRef.current) return;

        const newSegments: TranscriptSegment[] = result.entries
          .filter((e) => e.messageType !== 'system_event')
          .map((e) => ({
            segmentId: `${e.userId}-${e.startTime}`,
            speakerId: e.userId,
            speakerName: e.userName,
            text: e.text,
            startedAt: e.startTime,
            updatedAt: e.timestamp,
            status: 'finalized' as const,
            sessionOffsetMs: 0,
            providerToApiMs: 0,
            apiToUiMs: 0,
            speakerRole: e.participantRole,
          }));

        setSegments((prev) => {
          // Deduplicate: remove from prev any segment that already exists in newSegments
          const newIds = new Set(newSegments.map((s) => s.segmentId));
          const filtered = prev.filter((s) => !newIds.has(s.segmentId));
          return [...newSegments, ...filtered];
        });

        const newBefore = Math.max(0, before - result.sentEntries);
        olderBeforeIndexRef.current = newBefore;
        setHasMoreTranscript(newBefore > 0);
        setIsLoadingOlderTranscript(false);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setIsLoadingOlderTranscript(false);
      });
  }, [pinnedSessionUuid, isLoadingOlderTranscript]);

  return {
    status,
    sessionUuid,
    zoomMeetingUuid,
    dealUuid,
    dealTitle,
    segments,
    transcriptChunks,
    recommendations,
    errorMessage,
    isConnected,
    rawEventsByType,
    systemEventLog,
    rawTranscriptLog,
    hasMoreTranscript,
    isLoadingOlderTranscript,
    loadOlderTranscript,
    refreshZoomStatus,
  };
}
